<template>
  <g
    class="measure-annotation"
    :class="{ selected, editing }"
  >
    <!-- Ligne de mesure -->
    <line
      :x1="annotation.data.x1"
      :y1="annotation.data.y1"
      :x2="annotation.data.x2"
      :y2="annotation.data.y2"
      :stroke="selected ? '#4a9eff' : (annotation.color || '#ff00ff')"
      :stroke-width="selected ? (strokeWidth + 1) : strokeWidth"
      stroke-linecap="round"
      class="measure-line"
      :class="{ draggable: selected }"
      @click.stop="handleClick"
      @mousedown.stop="selected ? handleStartLineDrag($event) : null"
    />

    <!-- Flèches aux extrémités -->
    <g>
      <!-- Flèche début -->
      <path
        :d="getArrowPath(annotation.data.x1, annotation.data.y1, annotation.data.x2, annotation.data.y2)"
        :fill="selected ? '#4a9eff' : (annotation.color || '#ff00ff')"
      />
      <!-- Flèche fin -->
      <path
        :d="getArrowPath(annotation.data.x2, annotation.data.y2, annotation.data.x1, annotation.data.y1)"
        :fill="selected ? '#4a9eff' : (annotation.color || '#ff00ff')"
      />
    </g>

    <!-- Étiquette de distance -->
    <g>
      <rect
        :x="labelX - labelWidth / 2"
        :y="labelY - labelHalfHeight"
        :width="labelWidth"
        :height="labelHeight"
        fill="rgba(26, 26, 26, 0.95)"
        :stroke="selected ? '#4a9eff' : (annotation.color || '#ff00ff')"
        :stroke-width="labelStrokeW"
        :rx="8 / pixelsPerUser"
      />
      <text
        :x="labelX"
        :y="labelY"
        :font-size="labelFontSize"
        font-weight="600"
        :fill="selected ? '#4a9eff' : (annotation.color || '#ff00ff')"
        text-anchor="middle"
        dominant-baseline="middle"
        pointer-events="none"
      >
        {{ distanceText }}
      </text>
    </g>

    <!-- Points de contrôle (visibles si sélectionné) -->
    <g v-if="selected">
      <!-- Point de départ -->
      <circle
        :cx="annotation.data.x1"
        :cy="annotation.data.y1"
        :r="endpointRadius"
        fill="#4a9eff"
        stroke="white"
        :stroke-width="endpointStroke"
        class="control-point"
        @mousedown.stop="handleStartDrag($event, 'start')"
      />
      <!-- Point d'arrivée -->
      <circle
        :cx="annotation.data.x2"
        :cy="annotation.data.y2"
        :r="endpointRadius"
        fill="#4a9eff"
        stroke="white"
        :stroke-width="endpointStroke"
        class="control-point"
        @mousedown.stop="handleStartDrag($event, 'end')"
      />
    </g>
  </g>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import type { Annotation } from '@api/annotations.api';
import {
  ANNOTATION_DEFAULT_STROKE_PX,
  ANNOTATION_HANDLE_RADIUS_PX,
  ANNOTATION_HANDLE_STROKE_PX,
  ANNOTATION_MEASURE_ARROW_PX,
  ANNOTATION_MEASURE_LABEL_FONT_PX,
} from '@/constants/annotations';

const props = defineProps<{
  annotation: Annotation;
  selected: boolean;
  svgRef?: SVGSVGElement | null;
  scale?: number | null;
  canvasPixelsPerUser?: number;
}>();

const emit = defineEmits<{
  select: [annotation: Annotation];
  update: [annotation: Annotation, data: any, save?: boolean];
  delete: [annotation: Annotation];
}>();

const editing = ref(false);
const dragging = ref(false);
const dragPoint = ref<'start' | 'end' | 'line' | null>(null);
const dragStartPos = ref({ x: 0, y: 0 });
const dragStartLine = ref({ x1: 0, y1: 0, x2: 0, y2: 0 });
const lastDraggedData = ref<any | null>(null);

const pixelsPerUser = computed(() => Math.max(props.canvasPixelsPerUser ?? 1, 0.01));
const strokeWidth = computed(() => props.annotation.stroke_width ?? ANNOTATION_DEFAULT_STROKE_PX);
const endpointRadius = computed(() => ANNOTATION_HANDLE_RADIUS_PX / pixelsPerUser.value);
const endpointStroke = computed(() => ANNOTATION_HANDLE_STROKE_PX / pixelsPerUser.value);
const labelStrokeW = computed(() => 2 / pixelsPerUser.value);

// Calcul de la distance en pixels
const distance = computed(() => {
  const dx = props.annotation.data.x2 - props.annotation.data.x1;
  const dy = props.annotation.data.y2 - props.annotation.data.y1;
  return Math.sqrt(dx * dx + dy * dy);
});

const distanceText = computed(() => {
  const distPx = Math.round(distance.value);
  
  // Si on a une échelle, afficher en mm
  if (props.scale && props.scale > 0) {
    const distMm = distPx / props.scale;
    return `${Math.round(distMm)} mm`;
  }
  
  // Sinon afficher en pixels
  return `${distPx} px`;
});

const labelX = computed(() => {
  return (props.annotation.data.x1 + props.annotation.data.x2) / 2;
});

const labelY = computed(() => {
  return (props.annotation.data.y1 + props.annotation.data.y2) / 2;
});

const labelFontSize = computed(
  () => ANNOTATION_MEASURE_LABEL_FONT_PX / pixelsPerUser.value
);
const labelHeight = computed(() => labelFontSize.value * 1.35);
const labelHalfHeight = computed(() => labelHeight.value / 2);

const labelWidth = computed(() => {
  const padUser = 24 / pixelsPerUser.value;
  return distanceText.value.length * labelFontSize.value * 0.55 + padUser * 2;
});

// Flèches : taille à l’écran stable (comme les poignées)
const getArrowPath = (x: number, y: number, targetX: number, targetY: number): string => {
  const angle = Math.atan2(targetY - y, targetX - x);
  const arrowLength = ANNOTATION_MEASURE_ARROW_PX / pixelsPerUser.value;
  
  const x1 = x + Math.cos(angle - Math.PI / 6) * arrowLength;
  const y1 = y + Math.sin(angle - Math.PI / 6) * arrowLength;
  const x2 = x + Math.cos(angle + Math.PI / 6) * arrowLength;
  const y2 = y + Math.sin(angle + Math.PI / 6) * arrowLength;
  
  return `M ${x} ${y} L ${x1} ${y1} L ${x2} ${y2} Z`;
};

const handleClick = () => {
  if (!props.selected) {
    emit('select', props.annotation);
  }
};

const handleStartDrag = (event: MouseEvent, point: 'start' | 'end') => {
  if (!props.svgRef) return;
  
  dragging.value = true;
  dragPoint.value = point;
  lastDraggedData.value = null;
  event.preventDefault();
  event.stopPropagation();
};

const handleStartLineDrag = (event: MouseEvent) => {
  if (!props.svgRef) return;
  
  dragging.value = true;
  dragPoint.value = 'line';
  lastDraggedData.value = null;
  
  const svg = props.svgRef;
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
  
  dragStartPos.value = { x: svgP.x, y: svgP.y };
  dragStartLine.value = {
    x1: props.annotation.data.x1,
    y1: props.annotation.data.y1,
    x2: props.annotation.data.x2,
    y2: props.annotation.data.y2,
  };
  
  event.preventDefault();
  event.stopPropagation();
};

const handleDragMove = (event: MouseEvent) => {
  if (!dragging.value || !dragPoint.value || !props.svgRef) return;
  
  const svg = props.svgRef;
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
  
  const newData = { ...props.annotation.data };
  
  if (dragPoint.value === 'line') {
    // Déplacer toute la ligne
    const deltaX = svgP.x - dragStartPos.value.x;
    const deltaY = svgP.y - dragStartPos.value.y;
    
    newData.x1 = dragStartLine.value.x1 + deltaX;
    newData.y1 = dragStartLine.value.y1 + deltaY;
    newData.x2 = dragStartLine.value.x2 + deltaX;
    newData.y2 = dragStartLine.value.y2 + deltaY;
  } else if (dragPoint.value === 'start') {
    newData.x1 = svgP.x;
    newData.y1 = svgP.y;
  } else if (dragPoint.value === 'end') {
    newData.x2 = svgP.x;
    newData.y2 = svgP.y;
  }
  
  lastDraggedData.value = newData;
  emit('update', props.annotation, newData, false);
};

const handleDragEnd = () => {
  if (dragging.value) {
    const finalData = lastDraggedData.value;
    dragging.value = false;
    dragPoint.value = null;
    lastDraggedData.value = null;
    if (finalData) {
      emit('update', props.annotation, finalData, true);
    }
  }
};

onMounted(() => {
  window.addEventListener('mousemove', handleDragMove);
  window.addEventListener('mouseup', handleDragEnd);
});

onUnmounted(() => {
  window.removeEventListener('mousemove', handleDragMove);
  window.removeEventListener('mouseup', handleDragEnd);
});
</script>

<style scoped>
.measure-annotation {
  cursor: pointer;
}

.measure-line {
  transition: stroke 0.2s, stroke-width 0.2s;
}

.measure-line:hover {
  opacity: 0.92;
}

.measure-line.draggable {
  cursor: move;
}

.control-point {
  cursor: move;
}
</style>
